import { FC } from "react";
import { useNavigate } from "react-router";

import { RefreshCw, Plus } from "lucide-react";

import { CreatePlanAction } from "@vviinn/user-managment-client-ts/dist/models/CreatePlanAction";
import { CreateReportAction } from "@vviinn/user-managment-client-ts";

import { unblockScroll } from "../../../../app/hooks/useBlockScroll";
import { SlideOverWrapper } from "../../../../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import { EXTRA_LARGE_ICON_SIZE_STYLES } from "../../../../ui/styles";
import { BooleanVoidFunc } from "../../../../helpers/functions/types";
import { CompanyPricingItemTypes } from "./types";

type CompanySlideOverData = {
  title: string;
  text: string;
  fallbackAcceptText: string;
  icon: React.ReactNode;
};

const MODAL_ICON_STYLES = `text-black dark:text-white bg-white dark:bg-black rounded-full ${EXTRA_LARGE_ICON_SIZE_STYLES}`;

type SlideOverPricing = Exclude<CompanyPricingItemTypes, "invoices">;

const companySlideOverProps: {
  [key in SlideOverPricing]: CompanySlideOverData;
} = {
  plans: {
    title: "Creating New Plan",
    icon: <Plus className={MODAL_ICON_STYLES} />,
    text: "Are you sure you want to create a new plan?",
    fallbackAcceptText: "Create",
  },
  reports: {
    title: "Generating Reports",
    icon: <RefreshCw className={MODAL_ICON_STYLES} />,
    text: "Are you sure you want to generate reports?",
    fallbackAcceptText: "Generate",
  },
};

export const CompanyPricingOverlay: FC<{
  open: boolean;
  setOpen: BooleanVoidFunc;
  type: SlideOverPricing;
  itemCreateAction: CreatePlanAction | CreateReportAction;
}> = ({ open, setOpen, type, itemCreateAction }) => {
  const navigate = useNavigate();

  const { title, icon, text, fallbackAcceptText } = companySlideOverProps[type];

  const fallbackUrlCommonPart = `/company/${itemCreateAction.parameters.form.companyId}/contract/${itemCreateAction.parameters.form.contractId}/`;
  const fallbackUrlCustomPart =
    type === "plans"
      ? `${itemCreateAction.formType}/new`
      : "reports/regenerate";
  const fallbackUrl = `${fallbackUrlCommonPart}${fallbackUrlCustomPart}`;

  return (
    <SlideOverWrapper
      open={open}
      onClose={() => setOpen(false)}
      title={title}
      buttonProps={{
        title: itemCreateAction.label ?? fallbackAcceptText,
        onClick: () => {
          unblockScroll();
          navigate(itemCreateAction.navigateUrl ?? fallbackUrl);
          return Promise.resolve(true);
        },
      }}
    >
      <div className="text-primary flex flex-col items-center gap-6 pt-6 text-center">
        {icon}
        {text}
      </div>
    </SlideOverWrapper>
  );
};
